import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiMenu, FiX, FiUser, FiLogOut, FiAward, FiSettings } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

const MobileOptimizedHeader = ({ title, subtitle }) => {
  const { user, logout, isProfessional } = useAuth();
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  return (
    <>
      <motion.header 
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-40"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary-dark rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">BQ</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">{title || 'BQ SyncSpec'}</h1>
              {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
            </div>
          </div>
          
          <button
            onClick={() => setShowMobileMenu(true)}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiMenu} size={20} />
          </button>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {showMobileMenu && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowMobileMenu(false)}
          >
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="absolute right-0 top-0 h-full w-80 max-w-[90vw] bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200">
                    <img 
                      src={user?.avatar} 
                      alt={user?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{user?.name}</p>
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      {isProfessional ? (
                        <>
                          <SafeIcon icon={FiAward} className="text-yellow-500" size={12} />
                          Professional
                        </>
                      ) : (
                        'Free Plan'
                      )}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowMobileMenu(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <SafeIcon icon={FiX} size={20} />
                </button>
              </div>

              {/* Menu Items */}
              <div className="p-4 space-y-2">
                <button className="flex items-center gap-3 w-full p-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                  <SafeIcon icon={FiUser} size={20} />
                  <span>Profile</span>
                </button>
                
                <button className="flex items-center gap-3 w-full p-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                  <SafeIcon icon={FiSettings} size={20} />
                  <span>Settings</span>
                </button>

                {!isProfessional && (
                  <button className="flex items-center gap-3 w-full p-3 text-left bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-lg font-medium">
                    <SafeIcon icon={FiAward} size={20} />
                    <span>Upgrade to Pro</span>
                  </button>
                )}

                <div className="pt-2 border-t border-gray-200">
                  <button 
                    onClick={() => {
                      logout();
                      setShowMobileMenu(false);
                    }}
                    className="flex items-center gap-3 w-full p-3 text-left text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiLogOut} size={20} />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MobileOptimizedHeader;